import React from 'react';
import {
  HOUR_LABEL, HOURMINFORMAT, MAPPER_UP_AND_DOWN_ASC_DESC,
  NO_DATA,
  PERCENTAGE_MEASURE, STATUS_AVAILABLE,
} from 'consts';
import useFormatter from 'hook/useFormatter';
import moment from 'moment';
import {
  FooterText, GreyText,
} from 'utils';

const useTableRender = (t) => {
  const {
    epoch: {
      msToHs, timeStampSecondsConverter, formatTime,
      formatDateTime, minToHs,
    },
    currency: { formatCurrency },
    unit: { formatElectricEnergy },
    number: { formatDecimal },
  } = useFormatter();

  const defaultColumnRender = (data) => <GreyText>{data ?? NO_DATA}</GreyText>;
  const defaultCurrencyRender = (data) => <GreyText>{formatCurrency(Number(data))}</GreyText>;
  const defaultDateTimeRender = (data) => <GreyText>{formatDateTime(data)}</GreyText>;
  const defaultTimeRender = (data) => <GreyText>{formatTime(data)}</GreyText>;
  const defaultMsToHrsRender = (data) => <GreyText>{msToHs(data)}</GreyText>;
  const defaultMinToHrsRender = (data) => <GreyText>{minToHs(data)}</GreyText>;
  const defaultFooterRender = (data, formatter, from) => {
    const total = data?.reduce((aggr, current) => (Number(current) ? aggr + Number(current) : aggr), 0);
    return (<FooterText>{formatter ? formatter(total, from) : total}</FooterText>);
  };
  const footerMsecToHours = (rowVal) => defaultFooterRender(rowVal, msToHs);
  const footerCurrency = (rowVal) => defaultFooterRender(rowVal, formatCurrency);
  const footerKwh = (rowVal, from) => defaultFooterRender(rowVal, formatElectricEnergy, from);

  const nullColumn = defaultColumnRender();

  const sortByField = (sortingField, sort, setSortingParameter) => setSortingParameter({
    sortingField,
    direction: MAPPER_UP_AND_DOWN_ASC_DESC[sort.direction],
  });

  const formatDuration = (value) => `${moment.duration(value * 3600 * 1000).format(HOURMINFORMAT)}${HOUR_LABEL}`;

  const timeAndPercentageCell = (
    time,
    percentage,
  ) => {
    const duration = time ? formatDuration(time) : NO_DATA;
    const perc = percentage ? `${percentage}${PERCENTAGE_MEASURE}` : NO_DATA;

    return <GreyText>{`${duration} ${perc}`}</GreyText>;
  };

  const percentageCell = (
    data,
  ) => <GreyText>{`${Number(data) ? data.toFixed(2).replace('.', ',') : NO_DATA} ${PERCENTAGE_MEASURE}`}</GreyText>;

  const checkIfValueIsValid = (val) => !Number.isNaN(val) && val !== null && val !== undefined && !!val;
  const checkIfTimeIsValid = (val) => checkIfValueIsValid(val.time);
  const checkIfPercentageIsValid = (val) => checkIfValueIsValid(val.percentage);

  const calcValue = (data) => {
    const timeData = data.filter(checkIfTimeIsValid);
    const timeSum = timeData.reduce((prev, next) => prev + next.time, 0);

    const percentageData = data.filter(checkIfPercentageIsValid);
    const percentageSum = percentageData.reduce((prev, next) => prev + next.percentage, 0);

    // Moltiplichiamo per 3600 e per 1000 per trasdormarlo in mls e non perdere i minuti
    const duration = timeSum ? formatDuration(timeSum) : NO_DATA;
    const percentage = percentageSum ? `${formatDecimal(percentageSum / percentageData.length)}${PERCENTAGE_MEASURE}` : NO_DATA;

    return { duration, percentage };
  };

  const timeAndPercentageFooter = (
    data, timeKey, percentageKey,
  ) => {
    const { duration, percentage } = calcValue(data.map((e) => ({
      time: e[timeKey],
      percentage: e[percentageKey],
    })));

    return <FooterText>{`${duration} ${percentage}`}</FooterText>;
  };

  const timeStampColumn = (value) => (
    <GreyText>
      {timeStampSecondsConverter(value)}
    </GreyText>
  );

  const accountingFieldRenderer = (data, status) => {
    let toRet = nullColumn;

    if (!!status) {
      if (status === STATUS_AVAILABLE) toRet = data;
      else toRet = t(status);
    }

    return toRet;
  };

  return {
    accountingFieldRenderer,
    defaultColumnRender,
    defaultCurrencyRender,
    defaultDateTimeRender,
    defaultFooterRender,
    defaultMinToHrsRender,
    defaultMsToHrsRender,
    defaultTimeRender,
    footerCurrency,
    footerKwh,
    footerMsecToHours,
    nullColumn,
    percentageCell,
    sortByField,
    timeAndPercentageCell,
    timeAndPercentageFooter,
    timeStampColumn,
  };
};

export default useTableRender;
